import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { blogPosts } from '../data/blogPosts';
import { PageHero } from '../components/shared/PageHero';

function Blog() {
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  // 提取唯一分类
  const categories = ['Todos', ...Array.from(new Set(blogPosts.map(post => post.category)))];

  const filteredPosts = selectedCategory === 'Todos'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero区域 */}
      <PageHero
        title="Blog"
        subtitle="Noticias, guías y consejos sobre movilidad eléctrica"
      />

      <div className="container-max section-padding py-16">
        {/* 分类筛选 */}
        <AnimatedSection>
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-neutral-900 text-white'
                    : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </AnimatedSection>

        {/* 博客网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post, index) => (
            <AnimatedSection key={post.id} delay={index * 0.05}>
              <Link to={`/blog/${post.slug}`} className="block group">
                <motion.article
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 h-full flex flex-col"
                  whileHover={{ y: -8 }}
                >
                  {/* 图片区域 */}
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* 分类标签 */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/95 backdrop-blur-sm text-neutral-800 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  {/* 内容区域 */}
                  <div className="p-6 flex flex-col flex-1">
                    {/* 日期 */}
                    <div className="flex items-center gap-2 text-neutral-400 text-sm mb-3">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(post.publishDate)}</span>
                    </div>

                    {/* 标题 */}
                    <h2 className="text-lg font-bold text-neutral-900 mb-3 line-clamp-2 group-hover:text-neutral-600 transition-colors">
                      {post.title}
                    </h2>

                    {/* 摘要 */}
                    <p className="text-neutral-500 text-sm leading-relaxed line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>

                    {/* 阅读更多 */}
                    <div className="mt-5 flex items-center text-neutral-900 font-medium text-sm group-hover:text-neutral-600 transition-colors">
                      <span>Leer más</span>
                      <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </motion.article>
              </Link>
            </AnimatedSection>
          ))}
        </div>

        {/* 空状态 */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-neutral-500 text-lg">
              No hay artículos en esta categoría
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Blog;
